import React, { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import { useItemSettings } from '@hooks/useItemSettings'
import { uploadImage } from '@helpers/api'
import { useCreateSection } from '@hooks/mutations/useCreateSection'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Button from '@components/Button'
import Heading from '@components/Heading'
import GridColumns from '@components/GridColumns'
import Divider from '@components/Divider'
import Form from '@components/Form'
import Text from '@components/Text'
import ButtonWithLoader from '@components/ButtonWithLoader'

const CreateSectionPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { userId },
  } = state

  const { schoolYears, subjectOptions, levelOptions, tags } = useItemSettings()

  let navigate = useNavigate()

  const { post, isPosting } = useCreateSection()

  const { register, handleSubmit, errors, control, formState } = useForm({ mode: 'onChange' })
  const { isValid } = formState

  const onSubmit = async (data) => {
    let fileName = ''

    if (data.image.length > 0) {
      // We have image, upload it!
      try {
        fileName = await uploadImage(data.image[0])
      } catch (error) {
        console.log(error)
        // show toast here?
      }
    }

    const tagIdsToConnect = data.tags.reduce((acc, tag) => {
      if (tag.hasOwnProperty('id')) {
        return [...acc, tag.id]
      } else {
        return acc
      }
    }, [])

    const tagsToCreate = data.tags.reduce((acc, tag) => {
      if (!tag.hasOwnProperty('id')) {
        return [...acc, { name: tag.name }]
      } else {
        return acc
      }
    }, [])

    const res = await post({
      name: data.name,
      description: data.description,
      subject: data.subject,
      schoolYear: data.schoolYear,
      level: data.level,
      creator: userId,
      connectTags: tagIdsToConnect,
      createTags: tagsToCreate,
      image: fileName,
    })

    if (res.success && res.id) {
      dispatch(closePanel(PANELS.CREATE_SECTION))
      navigate(`avsnitt/${res.id}`)
    }
  }

  return (
    <>
      <div className='flex justify-between items-center'>
        <Text>Skriv ett eget avsnitt</Text>
        <Button
          handleClick={() => {
            dispatch(closePanel(PANELS.CREATE_SECTION))
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        ></Button>
      </div>

      <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
        Om mitt avsnitt
      </Heading>

      <Divider size={DIVIDER_SIZE.MEDIUM} />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Label>Välj årskurs*</Form.Label>
        {schoolYears.map((schoolYear, index) => {
          const { id, name } = schoolYear
          return (
            <Form.Radio
              key={`schoolYear${index}`}
              name={'schoolYear'}
              id={name}
              ref={register({ required: true })}
              label={`Årskurs ${name}`}
              value={id}
            />
          )
        })}
        {errors.schoolYear && (
          <Form.ErrorMessage>Årskurs behövs</Form.ErrorMessage>
        )}

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='image'>Välj en bild</Form.Label>
        <Form.ImageUpload name='image' id='image' ref={register()} />

        <Divider size={DIVIDER_SIZE.MEDIUM} />

        <GridColumns cols={2}>
          <div>
            <Form.Label htmlFor='subject'>Ämne*</Form.Label>
            <Form.Select
              name='subject'
              id='subject'
              ref={register({ required: true })}
              options={[{ value: '', name: 'Välj...' }, ...subjectOptions]}
            />
            {errors.subject && (
              <Form.ErrorMessage>Ämne behövs</Form.ErrorMessage>
            )}
          </div>
          <div>
            <Form.Label htmlFor='level'>Nivå*</Form.Label>
            <Form.Select
              name='level'
              id='level'
              ref={register({ required: true })}
              options={[{ value: '', name: 'Välj...' }, ...levelOptions]}
            />
            {errors.level && <Form.ErrorMessage>Nivå behövs</Form.ErrorMessage>}
          </div>
        </GridColumns>

        <Divider size={DIVIDER_SIZE.MEDIUM} />

        <Form.Label htmlFor='name'>Namnge avsnittet*</Form.Label>
        <Form.Input
          type={'text'}
          name='name'
          id='name'
          ref={register({ required: true })}
          placeholder='Titel'
        />
        {errors.name && <Form.ErrorMessage>Titel behövs</Form.ErrorMessage>}

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='name'>Beskriv avsnittet</Form.Label>
        <Controller
          control={control}
          name="description"
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Editor
              handleChange={(value) => onChange(value)}
              elementId={`create-section-description`}
              // placeholder='Beskrivning'
            />
          )}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='tags'>Lägg till taggar</Form.Label>
        <Controller
          control={control}
          name={'tags'}
          defaultValue={[]}
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Tags
              onChange={(tags) => onChange(tags)}
              tags={[]}
              suggestions={tags}
            />
          )}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <ButtonWithLoader
          type={BUTTON_TYPE.PRIMARY}
          isLoading={isPosting}
          disabledText={'Skapa avsnitt'}
          enabledText={'Skapa avsnitt'}
          disabled={!isValid || isPosting}
        />
      </Form>
    </>
  )
}

export default CreateSectionPanel
